import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AppButton } from '@/components/atoms';
import { Logout, Menu } from '@/components/atoms/AppIcon';
import ServiceTypeSelector from '@/components/molecules/ServiceTypeSelector';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import { useTheme, type ClinicServiceType } from '@shared/providers/theme/ThemeContext';

const normalizeServiceType = (raw: string | null | undefined): ClinicServiceType => {
  if (!raw) return null;
  const lc = raw.toLowerCase();
  if (lc === 'a2' || lc === 'r2' || lc === 'both') return lc as ClinicServiceType;
  return null;
};

/**
 * Authenticated shell for the massager checkin portal.
 * Header: logo (left) / レセPro title (center) / hamburger (right),
 * with an A2/R2 switcher below when the logged-in massager has both services.
 * Clicking the hamburger shows a small dropdown with the logout button.
 */
const MassagerLayout: React.FC = () => {
  const { massager, logout } = useMassagerAuthContext();
  const [menuOpen, setMenuOpen] = useState(false);

  const { serviceType, canSwitchServiceType, setServiceType, setClinicServiceType, themeConfig } =
    useTheme();

  useEffect(() => {
    setClinicServiceType(normalizeServiceType(massager?.service_type));
  }, [massager?.service_type, setClinicServiceType]);

  return (
    <div className="flex h-[100dvh] flex-col bg-gray-50 overflow-hidden">
      <header className="shrink-0 z-[1100] bg-massager-header shadow-sm">
        <div className="relative mx-auto flex w-full max-w-5xl items-center justify-end gap-3 px-3 py-3 sm:px-4">
          {canSwitchServiceType && (
            <div className="flex-1 min-w-0 max-w-[280px]">
              <ServiceTypeSelector
                variant="inline"
                currentType={serviceType}
                onTypeChange={setServiceType}
                canSwitch={canSwitchServiceType}
                a2Colors={themeConfig.A2.colors}
                r2Colors={themeConfig.R2.colors}
              />
            </div>
          )}

          {/* Hamburger + dropdown wrapper */}
          <div className="relative">
            <AppButton
              type="text"
              shape="circle"
              size="large"
              aria-label="メニュー"
              icon={<Menu />}
              onClick={() => setMenuOpen(v => !v)}
              className="!text-2xl !text-white hover:!bg-white/15"
            />

            {/* Logout dropdown — appears right below the hamburger button */}
            {menuOpen && (
              <>
                <div className="fixed inset-0" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full z-10 mt-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5">
                  <AppButton
                    type="text"
                    icon={<Logout />}
                    onClick={() => {
                      setMenuOpen(false);
                      void logout();
                    }}
                    className="whitespace-nowrap !text-gray-700 hover:!bg-gray-100"
                  >
                    ログアウト
                  </AppButton>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <main className="mx-auto flex w-full max-w-5xl flex-col px-3 py-4 sm:px-4 sm:py-6 min-h-full">
          <Outlet />
        </main>
      </div>

      <footer className="shrink-0 z-[1100] bg-massager-footer py-2 text-center text-sm text-gray-900">
        © 2026 レセプロ. All rights reserved.
      </footer>
    </div>
  );
};

export default MassagerLayout;
